import React, { memo, startTransition, useRef } from 'react'
import { useIntl } from 'react-intl'

import {
    AppSvg,
    Dropdown,
    FormattedHTMLMessage,
    LinkButton,
    Menu,
    newIcons,
    useBodyScrollLock,
} from '@web/walter-ui-kit'
import cn from 'classnames'
import styles from './style.module.css'
import { useData } from './useData'
import SearchInput from '~components/PageLayout/PageSearch/SearchInput/SearchInput'
import { useStopSearching } from '~components/App/useStopSearching'
import { useSearchSlotContext } from '~contexts/SearchContext'
import { lang } from '~components/PageLayout/PageSearch/lang'
import SearchMenuContent from '~components/PageLayout/PageSearch/SearchMenuContent'

interface Props {
    isOpenMobile?: boolean
    setOpenMobile?: (value: boolean) => void
}

function PageSearch({ isOpenMobile, setOpenMobile }: Props) {
    const intl = useIntl()
    const { searchString, isSearching, stopSearching } = useSearchSlotContext()
    const { searchRef: searchWrapperRef } = useStopSearching()
    const searchInputRef = useRef<HTMLInputElement | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout>()
    const searchMessage = intl.formatMessage(lang.search)

    const { searchData, trendingData, collectionData, isLoading } = useData(searchString)

    useBodyScrollLock(Boolean(isSearching || isOpenMobile))

    const handleOpenSearch = (open: boolean) => {
        startTransition(() => setOpenMobile?.(open))
    }

    const handleClick = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        handleOpenSearch(true)
        timeoutRef.current = setTimeout(() => searchInputRef.current?.focus(), 0)
    }

    return (
        <>
            {!isOpenMobile && (
                <div onClick={handleClick} className={styles.searchIcon}>
                    <AppSvg size={24} svgName={newIcons.ic24SearchOutline} path="new-icons" />
                </div>
            )}

            <div className={cn(styles.wrap, { [styles.mobileHide]: !isOpenMobile })}>
                <Dropdown
                    isDisabled={isLoading}
                    toggleOnClick={false}
                    closeOnScroll={false}
                    placement="bottom-end"
                    dataPointer="search_dropdown"
                    offsetOptions={{ x: 0, y: isOpenMobile ? -4 : 0 }}
                    onOpenChange={open => {
                        if (!open) {
                            handleOpenSearch(open)
                        }
                        if (!open && isSearching) {
                            stopSearching()
                        }
                    }}
                    content={
                        <div className={cn(styles.menu, { [styles.mobileHide]: !isOpenMobile })}>
                            <Menu>
                                <SearchMenuContent
                                    searchData={searchData}
                                    trendingData={trendingData}
                                    stopSearching={stopSearching}
                                    collectionData={collectionData}
                                    isSearching={Boolean(searchString)}
                                />
                            </Menu>
                        </div>
                    }
                >
                    <div className={styles.inputWrap}>
                        <SearchInput
                            isDisabled={isLoading}
                            placeholder={searchMessage}
                            wrapperRef={searchWrapperRef}
                            searchInputRef={searchInputRef}
                        />

                        {isOpenMobile && (
                            <div className={styles.cancelBtn}>
                                <LinkButton
                                    dataPointer="button_cancel_search"
                                    onClick={() => {
                                        stopSearching()
                                        handleOpenSearch(false)
                                    }}
                                >
                                    <FormattedHTMLMessage {...lang.searchCancel} />
                                </LinkButton>
                            </div>
                        )}
                    </div>
                </Dropdown>
            </div>
        </>
    )
}

export default memo(PageSearch)
